// =============================================================================
// Resources Tab - Gestione Risorse con Sidebar per Tipo
// =============================================================================

import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Users, Clock, Layers } from 'lucide-react';
import { useResources, useResourceTypes, useDeleteResource } from '../../api/hooks';
import { Resource, ResourceType } from '../../types';
import { ResourceFormModal } from './ResourceFormModal';
import { ResourceScheduleEditor } from './ResourceScheduleEditor';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  EmptyState,
  ErrorBlock,
  IconButton,
  LoadingBlock,
} from './_shared';

export function ResourcesTab() {
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [scheduleResource, setScheduleResource] = useState<Resource | null>(null);
  const { t } = useTranslation('settings');

  const { data: resourceTypes, isLoading: loadingTypes } = useResourceTypes();
  const { data: resources, isLoading: loadingResources, error: errorResources } = useResources(
    selectedTypeId || undefined
  );
  const deleteResource = useDeleteResource();

  // Auto-seleziona il primo tipo risorsa alla prima apertura
  useEffect(() => {
    if (!initialized && resourceTypes && resourceTypes.length > 0) {
      setSelectedTypeId(resourceTypes[0].id);
      setInitialized(true);
    }
  }, [resourceTypes, initialized]);

  // Conta risorse per tipo
  const { data: allResources } = useResources();
  const getResourceCountByType = (typeId: string) => {
    return allResources?.filter(r => r.resourceTypeId === typeId).length ?? 0;
  };

  const handleCreate = () => {
    setEditingResource(null);
    setIsModalOpen(true);
  };

  const handleEdit = (resource: Resource) => {
    setEditingResource(resource);
    setIsModalOpen(true);
  };

  const handleSchedule = (resource: Resource) => {
    setScheduleResource(resource);
  };

  const handleDelete = async (resource: Resource) => {
    if (window.confirm(t('resources.deleteConfirm', { name: resource.name }))) {
      try {
        await deleteResource.mutateAsync(resource.id);
      } catch (error) {
        console.error('Error deleting resource:', error);
        alert(t('resources.deleteError'));
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingResource(null);
  };

  // Get resource type info
  const getSelectedType = (): ResourceType | undefined => {
    return resourceTypes?.find(t => t.id === selectedTypeId);
  };

  if (loadingTypes) {
    return <LoadingBlock />;
  }

  return (
    <div className="flex h-full min-h-[500px] bg-white">
      {/* ===== SIDEBAR - Lista Tipi Risorsa ===== */}
      <aside className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col flex-shrink-0">
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-gray-700">
            <Layers className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-semibold uppercase tracking-wide">
              {t('resources.sidebarTitle')}
            </h3>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          <SidebarItem
            active={selectedTypeId === null}
            onClick={() => setSelectedTypeId(null)}
            icon={
              <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                <Users className="w-4 h-4 text-gray-600" />
              </div>
            }
            title={t('resources.allResources')}
            subtitle={`${allResources?.length ?? 0} ${t('resources.count')}`}
          />

          <div className="border-t border-gray-200 my-2" />

          {resourceTypes?.map((type) => (
            <SidebarItem
              key={type.id}
              active={selectedTypeId === type.id}
              onClick={() => setSelectedTypeId(type.id)}
              icon={
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${type.color || '#6B7280'}20` }}
                >
                  <span
                    className="w-3.5 h-3.5 rounded-full"
                    style={{ backgroundColor: type.color || '#6B7280' }}
                  />
                </div>
              }
              title={type.name}
              subtitle={`${getResourceCountByType(type.id)} ${t('resources.count')}`}
            />
          ))}

          {resourceTypes?.length === 0 && (
            <p className="text-sm text-gray-400 px-3 py-6 text-center">
              {t('resources.noTypesDefined')}
            </p>
          )}
        </div>
      </aside>

      {/* ===== MAIN CONTENT - Lista Risorse Filtrata ===== */}
      <div className="flex-1 flex flex-col bg-white min-w-0">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {selectedTypeId ? getSelectedType()?.name : t('resources.allResources')}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5 truncate">
              {resources?.length ?? 0} {t('resources.count')}
              {selectedTypeId && getSelectedType()?.description && (
                <span className="ml-2">· {getSelectedType()?.description}</span>
              )}
            </p>
          </div>
          <Button icon={Plus} onClick={handleCreate}>
            {t('resources.addResource')}
          </Button>
        </div>

        {/* Resources List */}
        <div className="flex-1 overflow-y-auto">
          {loadingResources ? (
            <LoadingBlock />
          ) : errorResources ? (
            <ErrorBlock message={t('resources.loadError')} />
          ) : resources?.length === 0 ? (
            <EmptyState
              icon={Users}
              title={t('resources.emptyMessage')}
              description={t('resources.emptyHint')}
            />
          ) : (
            <ul className="divide-y divide-gray-100">
              {resources?.map((resource) => (
                <li
                  key={resource.id}
                  className="flex items-center gap-4 px-6 py-3 hover:bg-gray-50 transition-colors"
                >
                  {/* Color & Name */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ring-1 ring-black/5"
                      style={{ backgroundColor: `${resource.color || '#6B7280'}20` }}
                    >
                      <span
                        className="w-5 h-5 rounded-full"
                        style={{ backgroundColor: resource.color || '#6B7280' }}
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{resource.name}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {resource.code || t('resources.noCode')}
                        {resource.description && ` · ${resource.description}`}
                      </p>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex-shrink-0">
                    {resource.isActive && resource.isBookable ? (
                      <Badge tone="green">{t('common:status.active')}</Badge>
                    ) : resource.isActive ? (
                      <Badge tone="amber">{t('common:status.notBookable')}</Badge>
                    ) : (
                      <Badge tone="gray">{t('common:status.inactive')}</Badge>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <IconButton
                      icon={Clock}
                      tone="primary"
                      label={t('resources.workSchedule')}
                      onClick={() => handleSchedule(resource)}
                    />
                    <IconButton
                      icon={Edit2}
                      tone="blue"
                      label={t('common:buttons.edit')}
                      onClick={() => handleEdit(resource)}
                    />
                    <IconButton
                      icon={Trash2}
                      tone="danger"
                      label={t('common:buttons.delete')}
                      onClick={() => handleDelete(resource)}
                      disabled={deleteResource.isPending}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ===== MODALS ===== */}
      
      {/* Resource Form Modal */}
      {isModalOpen && (
        <ResourceFormModal
          resource={editingResource}
          resourceTypes={resourceTypes ?? []}
          preselectedTypeId={selectedTypeId}
          onClose={handleModalClose}
        />
      )}

      {/* Schedule Editor Modal */}
      {scheduleResource && (
        <ResourceScheduleEditor
          resourceId={scheduleResource.id}
          resourceName={scheduleResource.name}
          onClose={() => setScheduleResource(null)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// SidebarItem — voce della lista tipi nella sidebar di ResourcesTab.
// Active state usa accent emerald con bordo sinistro per chiarezza visiva.
// -----------------------------------------------------------------------------
function SidebarItem({
  active,
  onClick,
  icon,
  title,
  subtitle,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
        active
          ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-200'
          : 'hover:bg-white/60 text-gray-700'
      }`}
    >
      {active && (
        <span className="absolute left-0 top-1/4 bottom-1/4 w-0.5 rounded-r bg-emerald-500" />
      )}
      {icon}
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate text-sm">{title}</p>
        {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
      </div>
    </button>
  );
}
